import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import { PRESET_FILTERS } from '../panels/ImageFiltersPanel';

export const overlayStyleFor = (placementId, method) => {
  const isSide = placementId === 'left' || placementId === 'right';

  const sizes = {
    print: {
      width: isSide ? '70px' : placementId === 'back' ? '40%' : '50%',
      height: isSide ? '70px' : placementId === 'back' ? '66%' : '80%',
      borderRadius: 0,
    },
    embroidery: { width: '70px', height: '70px', borderRadius: 0 },
  };

  const anchors = {
    front:
      method === 'print'
        ? { top: '54%', left: '50.5%', translate: '-50%, -50%' }
        : { top: '37%', left: '64%', translate: '-50%, -50%' },
    back:
      method === 'print'
        ? { top: '52%', left: '50%', translate: '-50%, -50%' }
        : { top: '30%', left: '50%', translate: '-50%, -50%' },
    left: { top: '32%', left: '50.2%', translate: '-50%, -50%' },
    right: { top: '32%', left: '50.2%', translate: '-50%, -50%' },
  };

  const s = sizes[method];
  const a = anchors[placementId] || anchors.front;

  return {
    width: s.width,
    height: s.height,
    borderRadius: `${s.borderRadius}px`,
    top: a.top,
    left: a.left,
    transform: `translate(${a.translate})`,
    position: 'absolute',
  };
};

export const buildImageFilter = (img) => {
  const preset = PRESET_FILTERS[img.presetFilter] || PRESET_FILTERS.original;

  return `
    ${preset.css}

    brightness(${img.brightness ?? 100}%)
    contrast(${img.contrast ?? 100}%)
    saturate(${img.saturation ?? 100}%)
    grayscale(${img.grayscale ?? 0}%)
    sepia(${img.sepia ?? 0}%)
    hue-rotate(${img.hueRotate ?? 0}deg)
    blur(${img.blur ?? 0}px)
  `;
};

// Reused single offscreen canvas for text measurement — avoids creating a
// new <canvas> on every call.
let _measureCanvas = null;
const getMeasureContext = () => {
  if (!_measureCanvas) {
    _measureCanvas = document.createElement('canvas');
  }
  return _measureCanvas.getContext('2d');
};

// Measures the actual rendered width of a text string for a given
// font size/family/weight/style — this is what the browser will really
// draw, so it's far more reliable than a fixed "average char width"
// guess (which breaks for large font sizes, bold/narrow fonts, etc).
export const measureTextWidth = (text, fontSize, fontFamily = 'Arial', bold = false, italic = false) => {
  const ctx = getMeasureContext();
  if (!ctx) return (text?.length || 0) * (fontSize || 16) * 0.6; // SSR / no-canvas fallback

  ctx.font = `${italic ? 'italic ' : ''}${bold ? '700 ' : '400 '}${fontSize || 16}px ${fontFamily || 'Arial'}`;
  return ctx.measureText(text || '').width;
};

export const createCurvedPath = (
    text,
    curveIntensity = 100,
    direction = "up",
    fontSize = 16,
    fontFamily = 'Arial',
    bold = false,
    italic = false,
) => {
    const baseRadius = 150;
    const radius = (baseRadius * curveIntensity) / 100;

    // The arc's chord must be at least as wide as the real rendered text,
    // or the browser will clip any characters that extend past the end
    // of the path. A small safety margin (8%) covers inter-glyph kerning
    // rounding differences between measurement and final render.
    const measuredWidth = measureTextWidth(text, fontSize, fontFamily, bold, italic);
    const width = Math.max(60, measuredWidth * 1.08);

    const x1 = -width / 2;
    const x2 = width / 2;

    const y = direction === "down"
        ? radius
        : -radius;

    const sweep = direction === "down" ? 0 : 1;

    return `M ${x1} ${y}
            A ${radius} ${radius} 0 0 ${sweep}
            ${x2} ${y}`;
};

// Single source of truth for curved-text geometry (path + viewBox), used
// by both the live editor (TShirtPlacement) and the read-only render
// (DesignOverlay, used in Preview/Product Info) so the two never drift
// out of sync with each other.
export const getCurvedTextMetrics = (text, {
  fontSize = 16,
  fontFamily = 'Arial',
  bold = false,
  italic = false,
  curveIntensity = 100,
  direction = 'up',
} = {}) => {
  const pathD = createCurvedPath(text, curveIntensity, direction, fontSize, fontFamily, bold, italic);

  const measuredWidth = measureTextWidth(text, fontSize, fontFamily, bold, italic);
  const chordWidth = Math.max(60, measuredWidth * 1.08);
  const radius = (150 * curveIntensity) / 100;

  // Padding around the chord/radius so ascenders, descenders, and the
  // arc's own curvature never get clipped by the viewBox.
  const halfWidth = chordWidth / 2 + 40;
  const arcRadius = Math.abs(radius) + fontSize * 1.5 + 20;

  return {
    pathD,
    chordWidth,
    radius,
    halfWidth,
    arcRadius,
    vbW: halfWidth * 2,
    vbH: arcRadius * 2,
  };
};

// NOTE: THIS IS THE KEY FIX
// The CurvedTextSVG component properly renders curved text for the preview
// It uses viewBox coordinate system (not CSS pixel sizing) to match the editor
const CurvedTextSVG = ({ t, displayText }) => {
  const textRef = useRef(null);
  const [box, setBox] = useState({
    width: 200,
    height: 100,
    x: 0,
    y: 0,
  });

  const pathId = 'curve-path-' + t.id;
  const curveText = displayText.replace(/\n/g, '');
  const fontSize = parseFloat(t.fontSize || 16);
  const arcDirection = t.arcDirection || 'up';
  const letterSpacing = arcDirection === 'down' ? '0.09em' : 'normal';

  const { pathD } = getCurvedTextMetrics(curveText, {
    fontSize,
    fontFamily: t.fontFamily || 'Arial',
    bold: !!t.bold,
    italic: !!t.italic,
    curveIntensity: t.curveIntensity || 100,
    direction: arcDirection,
  });

  useLayoutEffect(() => {
    if (!textRef.current) return;

    const textPathEl = textRef.current.querySelector('textPath');
    if (!textPathEl) return;

    let bbox;
    try {
      bbox = textPathEl.getBBox();
    } catch {
      bbox = {
        x: 0,
        y: -fontSize * 0.3,
        width: curveText.length * fontSize * 0.6,
        height: fontSize * 1.2,
      };
    }

    const padding = 1;
    const verticalPadding = 0;

    const adjustedBox = {
      width: Math.max(bbox.width, curveText.length * fontSize * 0.55) + padding * 2,
      height: bbox.height + verticalPadding * 2,
      x: bbox.x - padding,
      y: bbox.y - verticalPadding,
    };

    if (arcDirection === 'down') {
      adjustedBox.y = bbox.y - verticalPadding;
      adjustedBox.height = bbox.height + verticalPadding * 2;
    }

    // Ensure minimum dimensions based on text content
    const minWidth = Math.max(curveText.length * fontSize * 0.55, 20);
    const minHeight = Math.max(fontSize * 1.2, 20);
    adjustedBox.width = Math.max(adjustedBox.width, minWidth);
    adjustedBox.height = Math.max(adjustedBox.height, minHeight);

    // Center the box around the text bounding box
    const centerX = adjustedBox.x + adjustedBox.width / 2;
    const centerY = adjustedBox.y + adjustedBox.height / 2;
    adjustedBox.x = centerX - adjustedBox.width / 2;
    adjustedBox.y = centerY - adjustedBox.height / 2;

    setBox(adjustedBox);
  }, [curveText, fontSize, t.fontFamily, t.bold, t.italic, t.curveIntensity, arcDirection]);

  return (
    <svg
      ref={textRef}
      style={{
        display: 'inline-block',
        verticalAlign: 'baseline',
        minWidth: box.width,
        minHeight: box.height,
      }}
      width={box.width}
      height={box.height}
      viewBox={`${box.x} ${box.y} ${box.width} ${box.height}`}
      overflow="visible"
    >
      <defs>
        <path id={pathId} d={pathD} fill="none" />
      </defs>
      <text
        fontSize={fontSize}
        fontFamily={t.fontFamily || 'Arial'}
        fontWeight={t.bold ? '700' : '400'}
        fontStyle={t.italic ? 'italic' : 'normal'}
        fill={t.color || '#ffffff'}
        opacity={t.opacity ?? 1}
        textAnchor="middle"
        letterSpacing={letterSpacing}
        style={{
          textDecoration: t.underline ? 'underline' : 'none',
          textTransform: t.textTransform || 'none',
          WebkitTextStroke: t.strokeWidth ? t.strokeWidth + 'px ' + (t.strokeColor || '#000000') : undefined,
          paintOrder: 'stroke fill',
        }}
      >
        <textPath href={'#' + pathId} startOffset="50%">
          {displayText.replace(/\n/g, ' ')}
        </textPath>
      </text>
    </svg>
  );
};

const DesignOverlay = ({
  activeView,
  placementList = [],
  placementTexts = {},
  placementImages = {},
  methodPositions = {},
  boxSize = {},
}) => {
  const methodsForView = placementList.filter(
    (item) => item.placement === activeView
  );

  return (
    <>
      {methodsForView.map((methodItem) => {
        const { method, key: placementKey } = methodItem;
        const texts = placementTexts[placementKey] || [];
        const images = placementImages[placementKey] || [];
        const baseStyle = overlayStyleFor(activeView, method);

        return (
          <div key={placementKey}>
            <div
              style={{
                ...baseStyle,
                ...(method === 'embroidery'
                  ? {
                    transform: `${baseStyle.transform} translate(${methodPositions?.[placementKey]?.x || 0
                      }px, ${methodPositions?.[placementKey]?.y || 0}px)`,
                  }
                  : {}),
              }}
            >
              {/* Render images */}
              {images.map((img) => (
                <div
                  key={img.id}
                  style={{
                    position: 'absolute',
                    left: img.x || 0,
                    top: img.y || 0,
                    width: img.width || 120,
                    height: img.height || 120,
                    transform: `rotate(${img.rotation || 0}deg) skewX(${img.skewX || 0}deg) skewY(${img.skewY || 0}deg)`,
                    boxSizing: 'border-box',
                    opacity: img.opacity ?? 1,
                  }}
                >
                  <img
                    src={img.imageDataUrl}
                    alt=""
                    draggable={false}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'unset',
                      pointerEvents: 'none',
                      userSelect: 'none',
                      filter: buildImageFilter(img),
                    }}
                  />
                </div>
              ))}

              {/* Render text with curve support */}
              {texts.map((t) => {
                const x = typeof t.x === 'number' ? t.x : 0;
                const y = typeof t.y === 'number' ? t.y : 0;

                const displayText = (t.text || '')
                  .replace(/<br\s*\/?>/gi, '\n')
                  .replace(/<\/br>/gi, '\n')
                  .replace(/\\n/g, '\n');

                // Check if curve is enabled
                if (t.curve) {
                  return (
                    <div
                      key={t.id}
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        transform: 'translate(' + x + 'px, ' + y + 'px) rotate(' + (t.rotation || 0) + 'deg) scaleX(' + (t.flipH ? -1 : 1) + ') scaleY(' + (t.flipV ? -1 : 1) + ') skewX(' + (t.skewX || 0) + 'deg) skewY(' + (t.skewY || 0) + 'deg)',
                        whiteSpace: 'pre',
                        textAlign: t.textAlign || 'left',
                        textTransform: t.textTransform || 'none',
                        fontSize: t.fontSize || 16,
                        fontFamily: t.fontFamily || 'Arial',
                        color: t.color || '#ffffff',
                        fontWeight: t.bold ? '700' : '400',
                        fontStyle: t.italic ? 'italic' : 'normal',
                        textDecoration: t.underline ? 'underline' : 'none',
                        userSelect: 'none',
                        opacity: t.opacity ?? 1,
                        WebkitTextStroke: t.strokeWidth ? t.strokeWidth + 'px ' + (t.strokeColor || '#000000') : undefined,
                        textShadow: (t.shadowOffsetX || t.shadowOffsetY)
                          ? (t.shadowOffsetX || 0) + 'px ' + (t.shadowOffsetY || 0) + 'px 4px ' + (t.shadowColor || '#000000')
                          : 'none',
                      }}
                    >
                      <CurvedTextSVG
                        t={t}
                        displayText={displayText}
                      />
                    </div>
                  );
                }

                // Render regular straight text
                return (
                  <div
                    key={t.id}
                    style={{
                      display: 'inline-block',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      transform: 'translate(' + x + 'px, ' + y + 'px) rotate(' + (t.rotation || 0) + 'deg) skewX(' + (t.skewX || 0) + 'deg) skewY(' + (t.skewY || 0) + 'deg)',
                      whiteSpace: 'pre',
                      textAlign: t.textAlign || 'left',
                      textTransform: t.textTransform || 'none',
                      fontSize: t.fontSize || 16,
                      fontFamily: t.fontFamily || 'Arial',
                      color: t.color || '#ffffff',
                      fontWeight: t.bold ? '700' : '400',
                      fontStyle: t.italic ? 'italic' : 'normal',
                      textDecoration: t.underline ? 'underline' : 'none',
                      userSelect: 'none',
                      wordWrap: 'break-word',
                      textWrap: 'auto',
                      opacity: t.opacity ?? 1,
                      WebkitTextStroke: t.strokeWidth ? t.strokeWidth + 'px ' + (t.strokeColor || '#000000') : undefined,
                      textShadow: (t.shadowOffsetX || t.shadowOffsetY)
                        ? (t.shadowOffsetX || 0) + 'px ' + (t.shadowOffsetY || 0) + 'px 4px ' + (t.shadowColor || '#000000')
                        : 'none',
                    }}
                  >
                    {displayText.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < displayText.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default DesignOverlay;
